<template>
  <base-section
  id="cusco-tour"
  >
    <v-container>
    <base-section-heading
    title="Post-Conference Cusco Tour"
    color="secondary"
    align="left"
    :toColor="true"
    >
    Experience an unforgettable journey into the heart of the Andes: discover the history and culture of Cusco, explore the breathtaking Sacred Valley—including Chinchero, Moray, Maras, Ollantaytambo, and Pisac—and travel aboard a panoramic train to the majestic citadel of Machu Picchu. Those choosing the extended option will also witness the spectacular colors of Rainbow Mountain. A truly unique adventure combining Inca heritage, extraordinary landscapes, and the comfort of Casa Andina Premium hotels.
    </base-section-heading>

    <section aria-labelledby="tour-videos-heading" class="tour-videos mb-8">
      <h2 id="tour-videos-heading" class="text-h5 font-weight-bold primary--text mb-4">
        A Glimpse of Peru: Your Journey After LATS 2027
      </h2>
      <p class="text-body-1">
        Extend your conference experience with a journey through Cusco, the Sacred Valley,
        and Machu Picchu. Explore the places that make this post-conference tour memorable:
      </p>

      <v-row>
        <v-col
          v-for="video in tourVideos"
          :key="video.id"
          cols="12"
          md="6"
        >
          <v-card outlined class="tour-video-card">
            <div class="tour-video-player">
              <iframe
                :src="'https://www.youtube-nocookie.com/embed/' + video.id"
                :title="video.title + ' — tour preview video'"
                loading="lazy"
                referrerpolicy="strict-origin-when-cross-origin"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowfullscreen
              />
            </div>
            <div class="pa-5">
              <v-chip v-if="video.optional" small outlined color="secondary" class="mb-3">
                Optional Extension
              </v-chip>
              <h3 class="text-h6 font-weight-bold mb-2">{{ video.title }}</h3>
              <p class="text-body-1">{{ video.description }}</p>
              <div class="tour-video-links">
                <a :href="video.url" target="_blank" rel="noopener noreferrer">
                  Watch {{ video.title }} on YouTube
                  <v-icon small color="primary" aria-hidden="true">mdi-open-in-new</v-icon>
                </a>
                <a
                  v-if="video.additionalUrl"
                  :href="video.additionalUrl"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Watch another Ollantaytambo video
                  <v-icon small color="primary" aria-hidden="true">mdi-open-in-new</v-icon>
                </a>
              </div>
            </div>
          </v-card>
        </v-col>
      </v-row>

      <p class="text-body-1 mt-6 mb-0">
        Explore the four-day and five-day tour details below. Booking information will be
        available soon.
      </p>
    </section>

    <v-expansion-panels
    class="mt-4 mb-8"
    multiple
    >
      <v-expansion-panel>
        <v-expansion-panel-header>
          <div>
            <strong>Option 1 — Four-Day Tour</strong><br>
            Cusco, the Sacred Valley, and Machu Picchu
          </div>
        </v-expansion-panel-header>
        <v-expansion-panel-content>
          <p class="mt-4">
            <strong>April 17–20, 2027 | 4 Days / 3 Nights</strong><br>
            Minimum group size: 20 participants
          </p>
          <p>
            Following LATS 2027, conference participants and accompanying guests are invited to join an optional four-day excursion to Cusco, the Sacred Valley of the Incas and Machu Picchu. The program combines Peru's extraordinary archaeological heritage, spectacular Andean scenery and rich cultural traditions, with accommodation at <strong>Casa Andina Premium hotels</strong> in Cusco and the Sacred Valley.
          </p>

          <h3 class="mt-4 mb-2">Package Includes</h3>
          <ul>
            <li>Round-trip LATAM Airlines flights between Lima and Cusco</li>
            <li>One personal item and one carry-on bag of up to 10 kg</li>
            <li>Private airport–hotel–airport transfers</li>
            <li>Two nights at Casa Andina Premium Cusco</li>
            <li>One night at Casa Andina Premium Valle Sagrado</li>
            <li>Daily breakfast</li>
            <li>Full-day guided tour of the Sacred Valley</li>
            <li>Visits to Chinchero, Moray, the Maras Salt Mines, Ollantaytambo and Pisac</li>
            <li>Buffet lunch in Urubamba</li>
            <li>Full-day excursion to Machu Picchu</li>
            <li>Vistadome Observatory or Vistadome train service</li>
            <li>Lunch in Aguas Calientes</li>
            <li>Professional English- or Spanish-speaking tour guide</li>
            <li>Admission tickets to all attractions included in the itinerary</li>
            <li>Travel assistance throughout the excursion</li>
          </ul>

          <h3 class="mt-4 mb-2">Preliminary Itinerary</h3>
          <p><strong>Day 1 – Saturday, April 17: Lima–Cusco</strong><br>
          Morning flight from Lima to Cusco. Upon arrival, participants will be welcomed at the airport and transferred privately to Casa Andina Premium Cusco. The remainder of the day will be free for rest and acclimatization to Cusco's altitude.<br>
          Overnight at Casa Andina Premium Cusco</p>

          <p><strong>Day 2 – Sunday, April 18: Sacred Valley of the Incas</strong><br>
          After breakfast, the group will depart for a full-day guided tour of the Sacred Valley, including the Chinchero Textile Interpretation Center, Moray archaeological and agricultural terraces, the Maras Salt Mines, a buffet lunch in Urubamba, the Ollantaytambo Archaeological Site and the Pisac Archaeological Site. At the end of the tour, participants will be transferred to Casa Andina Premium Valle Sagrado.<br>
          Overnight at Casa Andina Premium Valle Sagrado</p>

          <p><strong>Day 3 – Monday, April 19: Machu Picchu</strong><br>
          Early transfer to the train station for the journey to Aguas Calientes aboard the Vistadome Observatory or Vistadome train. Upon arrival, the group will take the bus to the Historic Sanctuary of Machu Picchu for a professionally guided visit, exploring the principal agricultural, ceremonial and urban areas of the site. After the visit, the group will return to Aguas Calientes for lunch, followed by the train journey and private transfer back to Cusco.<br>
          Overnight at Casa Andina Premium Cusco</p>

          <p><strong>Day 4 – Tuesday, April 20: Cusco–Lima</strong><br>
          Breakfast at the hotel and free time according to the final flight schedule. At the appropriate time, participants will be transferred privately to Cusco Airport for the return flight to Lima.</p>

          <h3 class="mt-4 mb-2">Preliminary Flight Schedule</h3>
          <v-simple-table class="mb-4" style="max-width: 500px;">
            <template v-slot:default>
              <thead>
                <tr>
                  <th class="text-left">Route</th>
                  <th class="text-left">Departure</th>
                  <th class="text-left">Arrival</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Lima–Cusco</td>
                  <td>6:30 a.m.</td>
                  <td>7:50 a.m.</td>
                </tr>
                <tr>
                  <td>Cusco–Lima</td>
                  <td>7:55 p.m.</td>
                  <td>9:20 p.m.</td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
          <p>Flight schedules and fares are subject to change until reservations are confirmed.</p>

          <h3 class="mt-4 mb-2">Total Package Price</h3>
          <p>
            The prices below are the total package rates per person for the complete four-day, three-night excursion, based on a minimum group of 20 participants.
          </p>
          <v-simple-table class="mb-4" style="max-width: 500px;">
            <template v-slot:default>
              <thead>
                <tr>
                  <th class="text-left">Accommodation</th>
                  <th class="text-left">Price per person</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Single occupancy</td>
                  <td>US$1,313</td>
                </tr>
                <tr>
                  <td>Double or triple occupancy</td>
                  <td>US$1,101</td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
          <p>
            A non-refundable advance payment of US$499 per participant will be required to confirm the reservation. The remaining balance and payment deadlines will be communicated before booking. Prices are preliminary and subject to flight, train, admission ticket and hotel availability at the time of confirmation.
          </p>

          <h3 class="mt-4 mb-2">Not Included</h3>
          <ul>
            <li>Checked baggage or excess baggage</li>
            <li>Personal expenses</li>
            <li>Optional excursions or activities not listed in the itinerary</li>
            <li>Early check-in or late check-out charges</li>
            <li>Travel insurance</li>
            <li>Gratuities</li>
            <li>Any service not specifically listed as included</li>
          </ul>

          <h3 class="mt-4 mb-2">Important Information</h3>
          <p>
            Cusco is located approximately 3,400 meters above sea level. The first day has therefore been kept free to allow participants to rest and acclimatize.
          </p>
          <p>
            Only one small carry-on bag or backpack weighing up to 5 kg (11 lb) may be taken aboard the train to and from Machu Picchu. Larger luggage will remain at the designated hotel. Comfortable walking shoes, sun protection, rain protection and warm layered clothing are recommended.
          </p>
          <p>
            The itinerary, flight schedules and train services are preliminary and may be adjusted due to availability, weather or operational conditions.
          </p>

          <v-btn
            href="/Cuzco 4 days 4 nights.pdf"
            target="_blank"
            rounded
            color="primary"
            dark
            download
            style="padding: 15px"
            class="nowrap mt-2"
          >
            <v-icon left dark>mdi-file-download</v-icon>
            Download Full Itinerary (PDF)
          </v-btn>
        </v-expansion-panel-content>
      </v-expansion-panel>

      <v-expansion-panel>
        <v-expansion-panel-header>
          <div>
            <strong>Option 2 — Five-Day Tour</strong><br>
            Cusco, the Sacred Valley, Machu Picchu, and Rainbow Mountain
          </div>
        </v-expansion-panel-header>
        <v-expansion-panel-content>
          <p class="mt-4">
            <strong>April 17–21, 2027 | 5 Days / 4 Nights</strong><br>
            Minimum group size: 20 participants
          </p>
          <p>
            This extended option includes the same Cusco, Sacred Valley and Machu Picchu program offered in the four-day tour, with one additional day dedicated to visiting Rainbow Mountain (Vinicunca). The package also includes one additional night at Casa Andina Premium Cusco, breakfast and lunch during the Rainbow Mountain excursion, and the return flight to Lima on April 21.
          </p>

          <h3 class="mt-4 mb-2">Package Includes</h3>
          <ul>
            <li>Round-trip LATAM Airlines flights between Lima and Cusco</li>
            <li>One personal item and one carry-on bag of up to 10 kg</li>
            <li>Private airport–hotel–airport transfers</li>
            <li>Three nights at Casa Andina Premium Cusco</li>
            <li>One night at Casa Andina Premium Valle Sagrado</li>
            <li>Daily breakfast</li>
            <li>Full-day guided tour of the Sacred Valley</li>
            <li>Visits to Chinchero, Moray, the Maras Salt Mines, Ollantaytambo and Pisac</li>
            <li>Buffet lunch in Urubamba</li>
            <li>Full-day excursion to Machu Picchu</li>
            <li>Vistadome Observatory or Vistadome train service</li>
            <li>Lunch in Aguas Calientes</li>
            <li>Full-day excursion to Rainbow Mountain</li>
            <li>Breakfast and buffet lunch during the Rainbow Mountain excursion</li>
            <li>Professional English- or Spanish-speaking tour guide</li>
            <li>Admission tickets to all attractions included in the itinerary</li>
            <li>Travel assistance throughout the excursion</li>
          </ul>

          <h3 class="mt-4 mb-2">Preliminary Itinerary</h3>
          <p><strong>Day 1 – Saturday, April 17: Lima–Cusco</strong><br>
          Morning flight from Lima to Cusco. Upon arrival, participants will be welcomed at the airport and transferred privately to Casa Andina Premium Cusco. The remainder of the day will be free for rest and acclimatization to Cusco's altitude.<br>
          Overnight at Casa Andina Premium Cusco</p>

          <p><strong>Day 2 – Sunday, April 18: Sacred Valley of the Incas</strong><br>
          After breakfast, the group will depart for a full-day guided tour of the Sacred Valley, including the Chinchero Textile Interpretation Center, Moray archaeological and agricultural terraces, the Maras Salt Mines, a buffet lunch in Urubamba, the Ollantaytambo Archaeological Site and the Pisac Archaeological Site. At the end of the tour, participants will be transferred to Casa Andina Premium Valle Sagrado.<br>
          Overnight at Casa Andina Premium Valle Sagrado</p>

          <p><strong>Day 3 – Monday, April 19: Machu Picchu</strong><br>
          Early transfer to the train station for the journey to Aguas Calientes aboard the Vistadome Observatory or Vistadome train. Upon arrival, the group will take the bus to the Historic Sanctuary of Machu Picchu for a professionally guided visit, exploring the principal agricultural, ceremonial and urban areas of the site. After the visit, the group will return to Aguas Calientes for lunch, followed by the train journey and private transfer back to Cusco.<br>
          Overnight at Casa Andina Premium Cusco</p>

          <p><strong>Day 4 – Tuesday, April 20: Rainbow Mountain</strong><br>
          The group will be picked up from the hotel between approximately 4:30 and 4:45 a.m. for the journey to Rainbow Mountain. After approximately two hours of travel, the group will stop in the Cusipata area for an Andean breakfast, then continue for approximately one additional hour to the starting point of the hike. The ascent takes approximately 1 hour and 30 minutes, depending on the group's pace. At the viewpoint, participants will have approximately 30–45 minutes to enjoy the landscape, take photographs and learn about the mountain's geological formation. The group will then descend along the same route, and a buffet lunch will be served before returning to Cusco. Estimated arrival at the hotel: between 4:30 and 5:00 p.m.<br>
          Overnight at Casa Andina Premium Cusco</p>

          <p><strong>Day 5 – Wednesday, April 21: Cusco–Lima</strong><br>
          Breakfast at the hotel and free time according to the final flight schedule. At the appropriate time, participants will be transferred privately to Cusco Airport for the return flight to Lima.</p>

          <h3 class="mt-4 mb-2">Preliminary Flight Schedule</h3>
          <v-simple-table class="mb-4" style="max-width: 500px;">
            <template v-slot:default>
              <thead>
                <tr>
                  <th class="text-left">Route</th>
                  <th class="text-left">Departure</th>
                  <th class="text-left">Arrival</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Lima–Cusco</td>
                  <td>6:30 a.m.</td>
                  <td>7:50 a.m.</td>
                </tr>
                <tr>
                  <td>Cusco–Lima</td>
                  <td>4:55 p.m.</td>
                  <td>6:35 p.m.</td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
          <p>Flight schedules and fares are subject to change until reservations are confirmed.</p>

          <h3 class="mt-4 mb-2">Total Package Price</h3>
          <p>
            The prices below are the total package rates per person for the complete five-day, four-night excursion, based on a minimum group of 20 participants.
          </p>
          <v-simple-table class="mb-4" style="max-width: 500px;">
            <template v-slot:default>
              <thead>
                <tr>
                  <th class="text-left">Accommodation</th>
                  <th class="text-left">Price per person</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Single occupancy</td>
                  <td>US$1,469</td>
                </tr>
                <tr>
                  <td>Double or triple occupancy</td>
                  <td>US$1,184</td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
          <p>
            A non-refundable advance payment of US$499 per participant will be required to confirm the reservation. The remaining balance and payment deadlines will be communicated before booking. Prices are preliminary and subject to flight, train, admission ticket and hotel availability at the time of confirmation.
          </p>

          <h3 class="mt-4 mb-2">Not Included</h3>
          <ul>
            <li>Checked baggage or excess baggage</li>
            <li>Personal expenses</li>
            <li>Optional excursions or activities not listed in the itinerary</li>
            <li>Early check-in or late check-out charges</li>
            <li>Travel insurance</li>
            <li>Gratuities</li>
            <li>Any service not specifically listed as included</li>
          </ul>

          <h3 class="mt-4 mb-2">Important Information</h3>
          <p>
            Cusco is located approximately 3,400 meters above sea level. The first day has therefore been kept free to allow participants to rest and acclimatize.
          </p>
          <p>
            Rainbow Mountain is located at approximately 5,000 meters above sea level. The excursion requires a very early departure and involves hiking at high altitude. Participants should consider their physical condition carefully before selecting this option. Travel insurance is strongly recommended.
          </p>
          <p>
            Only one small carry-on bag or backpack weighing up to 5 kg (11 lb) may be taken aboard the train to and from Machu Picchu. Larger luggage will remain at the designated hotel. Comfortable walking shoes, sun protection, rain protection and warm layered clothing are recommended.
          </p>
          <p>
            The itinerary, flight schedules and train services are preliminary and may be adjusted due to availability, weather or operational conditions.
          </p>

          <v-btn
            href="/Cuzco 5 days 4 nights.pdf"
            target="_blank"
            rounded
            color="primary"
            dark
            download
            style="padding: 15px"
            class="nowrap mt-2"
          >
            <v-icon left dark>mdi-file-download</v-icon>
            Download Full Itinerary (PDF)
          </v-btn>
        </v-expansion-panel-content>
      </v-expansion-panel>
    </v-expansion-panels>

    </v-container>
  </base-section>
</template>

<script>
  export default {
    name: 'CuscoTour',

    data: () => ({
      tourVideos: [
        {
          id: '6f745Wz6yU8',
          title: 'Cusco',
          description: 'Begin in the historic heart of the Andes.',
          url: 'https://www.youtube.com/watch?v=6f745Wz6yU8',
        },
        {
          id: '_4G5EUpXm7g',
          title: 'The Sacred Valley and Casa Andina',
          description: 'Enjoy mountain scenery and a relaxing stay in the valley.',
          url: 'https://www.youtube.com/watch?v=_4G5EUpXm7g',
        },
        {
          id: 'DgfW7PgVkpM',
          title: 'Ollantaytambo',
          description: 'Discover a remarkable living Inca town.',
          url: 'https://www.youtube.com/watch?v=DgfW7PgVkpM',
          additionalUrl: 'https://www.youtube.com/watch?v=SbEFoAGbkVE',
        },
        {
          id: 'm3UYBZyTOfg',
          title: 'Machu Picchu',
          description: 'Experience the journey’s unforgettable highlight.',
          url: 'https://www.youtube.com/watch?v=m3UYBZyTOfg',
        },
        {
          id: '--KCmjqROac',
          title: 'Rainbow Mountain',
          description: 'Choose the five-day post-conference tour to add a visit to Rainbow Mountain and discover one of the Andes’ most colorful landscapes.',
          url: 'https://www.youtube.com/watch?v=--KCmjqROac',
          optional: true,
        },
      ],
    }),
  }
</script>

<style>
.v-application .grey--text.text--darken-1 {
  color: black !important;
}
.nowrap .v-btn__content {
  width: 100%;
  white-space: break-spaces;
  text-align: center;
}
</style>

<style scoped>
.tour-video-card {
  height: 100%;
  overflow: hidden;
}

.tour-video-player {
  position: relative;
  width: 100%;
  padding-bottom: 56.25%;
}

.tour-video-player iframe {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border: 0;
}

.tour-video-links {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.tour-video-links a {
  width: fit-content;
  max-width: 100%;
  line-height: 1.5;
  text-decoration: underline;
}
</style>
